import { useId, useState } from "react";
import { markAccountDeletionNotice } from "../../../app/accountDeletionNotice";
import {
  Alert,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "@bw/shared/hooks";
import type { PendingUserDeletionResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import {
  useDeletionStatus,
  useDataExport,
  useRequestDeletion,
} from "../hooks/usePrivacy";
import { BACK_TO_ACCOUNT, useAccountActive } from "../hooks/useAccountBack";

/**
 * PrivacyPage (/me/privacy) — CCPA self-serve: download my data + account
 * deletion.
 *
 * DOWNLOAD MY DATA → getMyDataExport (GET /me/data-export). The returned bundle
 * is the SERVER's data; the page offers it verbatim as a JSON file download.
 *
 * ACCOUNT DELETION is IMMEDIATE and PERMANENT (backend grace period = 0). The
 * confirm copy is TRUTHFUL: it states deletion is immediate/permanent and names
 * what is deleted/anonymized — it does NOT promise a "scheduled for <future>"
 * undo window the backend does not provide. The destructive action is
 * EMAIL-GATED: the user must type their account email, which is forwarded to
 * requestAccountDeletion (DELETE /me) where the backend rejects a mismatch
 * (anti-session-hijack). On success, the public login route carries a generic
 * terminal confirmation while AuthProvider immediately signs the user out — the
 * session is now invalid. No optimistic UI; a failure surfaces role="alert"
 * with state unchanged, and the confirm is re-entrancy-guarded (a double-click
 * fires the mutation once).
 *
 * PENDING-TOMBSTONE branch (defensive): if synchronous deletion was interrupted,
 * getDeletionStatus can return the durable recovery tombstone. The page renders
 * the SERVER's requested_at (formatted, never fabricated) and truthfully says
 * that deletion is still being processed. It offers no fictional cancel action.
 *
 * The AppShell supplies no page title, so this routed page OWNS its single <h1>
 * via <Page title="Privacy & data"> (one h1 per page).
 */

/**
 * Human "July 10, 2026" for a tombstone timestamp, best-effort. Formatted
 * in UTC so the displayed date is exactly the server's date — never shifted a day
 * by the viewer's local timezone (e.g. a UTC midnight rolling back to the 9th).
 */
function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function PrivacyPage() {
  const status = useDeletionStatus();
  const pending: PendingUserDeletionResponse | null = status.data ?? null;
  const active = useAccountActive();

  return (
    <Page title="Privacy & data" narrow back={active ? BACK_TO_ACCOUNT : undefined}>
      <Stack spacing={3} divider={<Divider flexItem />}>
        <DownloadSection />
        {pending ? (
          <PendingDeletionSection pending={pending} />
        ) : (
          <DeleteAccountSection />
        )}
      </Stack>
    </Page>
  );
}

/**
 * Download my data. The export is fetched on click (a mutation, not a query) and
 * offered as a JSON file — the data belongs to the user.
 */
function DownloadSection() {
  const exportMutation = useDataExport();

  function handleDownload() {
    exportMutation.mutate(undefined, {
      onSuccess: (bundle) => {
        try {
          const blob = new Blob([JSON.stringify(bundle, null, 2)], {
            type: "application/json",
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "my-data-export.json";
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
        } catch {
          // Download offering is best-effort (jsdom lacks URL.createObjectURL);
          // the fetch itself is the load-bearing action.
        }
      },
    });
  }

  return (
    <Stack component="section" spacing={1.5} sx={{ alignItems: "flex-start" }}>
      <Typography component="h2" variant="h6">
        Your data
      </Typography>
      <Typography color="text.secondary">
        Download a copy of the personal data we hold about you as a JSON file.
      </Typography>
      <Button
        variant="outlined"
        size="small"
        onClick={handleDownload}
        disabled={exportMutation.isPending}
      >
        {exportMutation.isPending ? "Preparing…" : "Download my data"}
      </Button>
      {exportMutation.isError ? (
        <Alert severity="error">
          We couldn&apos;t prepare your data export. Please try again.
        </Alert>
      ) : null}
    </Stack>
  );
}

/**
 * Delete my account — destructive, email-gated, IMMEDIATE + PERMANENT. The button
 * opens an inline confirm; the confirm requires the account email and forwards it
 * to the deletion. On success the page reaches a terminal deleted state and the
 * user is signed out. The public login route carries the final acknowledgement
 * because this guarded page may unmount immediately when authority clears.
 */
function DeleteAccountSection() {
  const { user, signOut } = useAuth();
  const requestDeletion = useRequestDeletion();
  const emailFieldId = useId();

  const [confirming, setConfirming] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [deleted, setDeleted] = useState(false);

  // Terminal honest state — the account has been deleted and the session is gone.
  // One element carries the "deleted" copy (the matcher expects a single match).
  if (deleted) {
    return (
      <Stack component="section" spacing={1.5} role="status">
        <Typography component="p" variant="h6">
          Your account has been deleted. Signing you out…
        </Typography>
      </Stack>
    );
  }

  function handleConfirm() {
    // Email gate, re-asserted here (not just on the button's disabled prop) so a
    // future refactor that splits the disabled logic can't open a bypass on an
    // IRREVERSIBLE action. The backend rejects a mismatch too — this is defence
    // in depth. Mirrors the disabled-prop comparison exactly.
    if (emailInput.trim() !== (user?.email ?? "").trim()) return;
    // Re-entrancy guard: a second click while the first is in flight is a no-op.
    if (requestDeletion.isPending) return;
    requestDeletion.mutate(emailInput, {
      onSuccess: () => {
        // AuthProvider clears local authority immediately. Persist only this
        // generic, same-tab acknowledgement so public /login can truthfully
        // confirm the completed deletion after this guarded page unmounts.
        markAccountDeletionNotice();
        setDeleted(true);
        // The session is now invalid — clear it (BFF logout + Firebase + local
        // state). Redirect to login is owned by the auth provider / guard.
        void signOut();
      },
    });
  }

  return (
    <Stack component="section" spacing={1.5} sx={{ alignItems: "flex-start" }}>
      <Typography component="h2" variant="h6">
        Delete my account
      </Typography>

      {!confirming ? (
        <Button
          color="error"
          variant="outlined"
          size="small"
          onClick={() => setConfirming(true)}
        >
          Delete my account
        </Button>
      ) : (
        <Stack spacing={1.5} sx={{ width: "100%", alignItems: "flex-start" }}>
          {/* HONEST copy: immediate + permanent, names what is removed. It does
              NOT promise a future scheduled date / a cancel-within-N-days window
              the immediate-deletion backend does not provide. Kept as ONE
              element, and worded so it does not read as a past-tense "your
              account has been deleted" confirmation (that state comes only AFTER
              a successful delete). */}
          <Alert severity="warning" role="note">
            This happens immediately and permanently. Your account and all
            personal data will be removed right now; records we must retain for
            legal or financial reasons are anonymized. This cannot be undone.
          </Alert>
          <TextField
            id={emailFieldId}
            type="email"
            size="small"
            fullWidth
            label="Type your account email to confirm"
            value={emailInput}
            autoComplete="off"
            onChange={(e) => setEmailInput(e.target.value)}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={handleConfirm}
              aria-busy={requestDeletion.isPending}
              disabled={emailInput.trim() !== (user?.email ?? "").trim()}
            >
              {requestDeletion.isPending ? "Deleting…" : "Confirm deletion"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setConfirming(false);
                setEmailInput("");
              }}
              disabled={requestDeletion.isPending}
            >
              Cancel
            </Button>
          </Stack>
          {requestDeletion.isError ? (
            <Alert severity="error">
              We couldn&apos;t delete your account. Please check the email and
              try again.
            </Alert>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Pending-deletion branch (defensive). Shows the SERVER's requested_at and the
 * truthful non-cancellable recovery state.
 */
function PendingDeletionSection({
  pending,
}: {
  pending: PendingUserDeletionResponse;
}) {
  return (
    <Stack component="section" spacing={1.5} sx={{ alignItems: "flex-start" }}>
      {/* The heading avoids the "scheduled for / scheduled…deletion / pending
          deletion" phrasing so exactly ONE element carries that text (the
          descriptive line below) for the matcher. */}
      <Typography component="h2" variant="h6">
        Account deletion
      </Typography>
      <Alert severity="info">
        Your account deletion is still being processed. It began on{" "}
        <time dateTime={pending.requested_at}>
          {formatDate(pending.requested_at)}
        </time>
        . Deletion cannot be stopped after it starts. If this message remains,
        contact support.
      </Alert>
    </Stack>
  );
}
